import type { MenuDraftItem } from "@how/website-core";
import { PALETTE_GROUNDS, readableOn } from "@how/website-core";
import { useBrand } from "../brand-queries";
import { useTenantRecord } from "../../tenant/queries";
import { PALETTE_INK, TYPEFACE_STACKS } from "../look-values";
import type { MenuName } from "../navigation-queries";

/** What a link reads as on the site: its label, or a placeholder while it is being typed. */
function label(item: MenuDraftItem): string {
  return item.label.trim() === "" ? "Untitled link" : item.label;
}

/**
 * The menu being edited, drawn as the site draws it (studio design): the header across the top of a page with each
 * link's sub-links opened under it, or the footer's columns. It follows every keystroke, before anything is saved.
 */
export function MenuPreview({
  menu,
  items,
}: {
  readonly menu: MenuName;
  readonly items: readonly MenuDraftItem[];
}) {
  const brand = useBrand().data?.brand;
  const churchName = useTenantRecord().data?.name ?? "Your church";
  const ground = brand === undefined ? "#f5f5f7" : PALETTE_GROUNDS[brand.palette];
  const ink = brand === undefined ? "#17171a" : PALETTE_INK[brand.palette];
  const accent = brand?.accent ?? "#1b4d3e";
  const font = brand === undefined ? undefined : TYPEFACE_STACKS[brand.typeface];
  const rule = `color-mix(in srgb, ${ink} 14%, transparent)`;
  return (
    <div className="ws-preview" aria-hidden>
      <div className="ws-menu-preview" style={{ background: ground, color: ink, fontFamily: font }}>
        {menu === "header" ? (
          <>
            <div className="ws-menu-preview__header" style={{ borderColor: rule }}>
              <span className="ws-preview__name">{churchName}</span>
              <span className="ws-menu-preview__links">
                {items.length === 0 ? (
                  <span className="ws-menu-preview__empty">No links yet</span>
                ) : (
                  items.map((item, i) => (
                    <span key={i} className="ws-menu-preview__link">
                      {label(item)}
                      {(item.children ?? []).length > 0 ? (
                        <span
                          className="ws-menu-preview__drop"
                          style={{ background: ground, borderColor: rule }}
                        >
                          {(item.children ?? []).map((child, ci) => (
                            <span key={ci}>{label(child)}</span>
                          ))}
                        </span>
                      ) : null}
                    </span>
                  ))
                )}
              </span>
            </div>
            <div className="ws-menu-preview__body">
              <span className="ws-menu-preview__headline">Welcome</span>
              <span className="ws-thumb__line" style={{ background: ink }} />
              <span className="ws-thumb__line ws-thumb__line--short" style={{ background: ink }} />
              <span
                className="ws-preview__primary ws-menu-preview__button"
                style={{ background: accent, color: readableOn(accent) }}
              >
                Plan a visit
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="ws-menu-preview__body ws-menu-preview__body--short">
              <span className="ws-thumb__line" style={{ background: ink }} />
              <span className="ws-thumb__line ws-thumb__line--short" style={{ background: ink }} />
            </div>
            <div className="ws-menu-preview__footer" style={{ borderColor: rule }}>
              <span className="ws-preview__name">{churchName}</span>
              {items.length === 0 ? (
                <span className="ws-menu-preview__empty">No links yet</span>
              ) : (
                <span className="ws-menu-preview__columns">
                  {items.map((item, i) => (
                    <span key={i} className="ws-menu-preview__column">
                      <strong>{label(item)}</strong>
                      {(item.children ?? []).map((child, ci) => (
                        <span key={ci}>{label(child)}</span>
                      ))}
                    </span>
                  ))}
                </span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
